import { useState, useEffect, useCallback, useMemo, useRef, memo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Save,
  BarChart3,
  ClipboardList,
  History,
  TrendingUp,
  TrendingDown,
  Minus,
  FileDown,
  FileSpreadsheet,
  AlertTriangle,
  Users,
  UserCheck,
  Printer,
} from "lucide-react";
import { ExcelExportLabel, excelExportButtonA11y } from "@/components/excelExportLabel";
import { exportQuickStatsHistoryPdf } from "@/lib/quickStatsHistoryPdfExport";
import { exportPerCellHistoryChartPdf } from "@/lib/quickStatsPerCellChartPdfExport";

const NativeSelect = ({ value, onChange, placeholder, children, className = "", disabled = false, id }) => (
  <select
    id={id}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    className={`flex h-11 w-full rounded-md border border-blue-200 bg-white px-3 py-2 text-base text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 sm:h-10 sm:text-sm ${className}`}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {children}
  </select>
);

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const defaultDashFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 84);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const defaultHistoryFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 180);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function parseNonNegInt(raw) {
  if (raw === "" || raw == null) return 0;
  const n = Number.parseInt(String(raw), 10);
  if (Number.isNaN(n) || n < 0) return 0;
  return Math.min(n, 5000);
}

function toYesNoChoice(flag) {
  return flag ? "yes" : "no";
}

function yesNoToBool(choice) {
  return choice === "yes";
}

function formatQuickStatYesNo(value) {
  if (value === true || value === "true" || value === 1) return "Sí";
  return "No";
}

function isQuickStatMeetingSuspended(row) {
  return Boolean(row?.meeting_suspended ?? row?.meetingSuspended);
}

function QuickStatsSuspensionBadge({ suspended }) {
  if (suspended) {
    return (
      <span className="inline-flex items-center rounded-md border border-red-300 bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-900">
        SUSPENDIDA
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-md border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-900">
      ACTIVA
    </span>
  );
}

function QuickStatsPlanningLabel({ held }) {
  if (held) {
    return <span className="font-semibold text-emerald-800">Sí</span>;
  }
  return <span className="font-medium text-amber-800/90">No</span>;
}

function trendLabel(t) {
  if (t === "growing") return { text: "En crecimiento", Icon: TrendingUp, className: "bg-emerald-100 text-emerald-900" };
  if (t === "declining") return { text: "A la baja", Icon: TrendingDown, className: "bg-rose-100 text-rose-900" };
  if (t === "stagnant") return { text: "Estancada", Icon: Minus, className: "bg-amber-100 text-amber-900" };
  return { text: "Pocos datos", Icon: Minus, className: "bg-gray-100 text-gray-700" };
}

/** Promedios y conteos solo sobre filas del historial ya filtradas (fechas + célula). */
function computeHistoryGeneralStats(historicoFiltrado) {
  const rows = Array.isArray(historicoFiltrado) ? historicoFiltrado : [];
  const cantidadReportes = rows.length;
  let totalGeneral = 0;
  let totalVisitas = 0;
  let totalMiembros = 0;
  for (const r of rows) {
    totalGeneral += Number(r.total_attendees) || 0;
    totalVisitas += (Number(r.regular_visits) || 0) + (Number(r.sporadic_visits) || 0);
    totalMiembros += Number(r.members_present) || 0;
  }
  const n = cantidadReportes > 0 ? cantidadReportes : 0;
  return {
    cantidadReportes,
    promedioAsistencia: n > 0 ? totalGeneral / n : 0,
    promedioVisitas: n > 0 ? totalVisitas / n : 0,
    promedioMiembros: n > 0 ? totalMiembros / n : 0,
  };
}

function formatHistoryAvg(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

function HistoryGeneralStatsPanel({ stats, loading }) {
  const cards = [
    {
      key: "count",
      label: "Cantidad de reportes procesados",
      value: loading ? "—" : String(stats.cantidadReportes),
      Icon: ClipboardList,
      accent: "border-blue-200 bg-white/90",
      iconClass: "text-blue-700",
    },
    {
      key: "avg-att",
      label: "Promedio semanal de asistencia",
      value: loading ? "—" : formatHistoryAvg(stats.promedioAsistencia),
      Icon: BarChart3,
      accent: "border-indigo-200 bg-white/90",
      iconClass: "text-indigo-700",
    },
    {
      key: "avg-vis",
      label: "Promedio de visitas por reunión",
      value: loading ? "—" : formatHistoryAvg(stats.promedioVisitas),
      Icon: UserCheck,
      accent: "border-emerald-200 bg-white/90",
      iconClass: "text-emerald-700",
    },
    {
      key: "avg-mem",
      label: "Promedio de miembros presentes",
      value: loading ? "—" : formatHistoryAvg(stats.promedioMiembros),
      Icon: Users,
      accent: "border-sky-200 bg-white/90",
      iconClass: "text-sky-700",
    },
  ];

  return (
    <section
      className="estadisticas-generales rounded-xl border border-blue-200/90 bg-gradient-to-br from-blue-50/95 via-white to-indigo-50/40 p-4 shadow-sm sm:p-5"
      aria-labelledby="historial-estadisticas-generales-title"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-blue-100/80 pb-3">
        <h3
          id="historial-estadisticas-generales-title"
          className="text-sm font-bold uppercase tracking-wider text-blue-950 sm:text-base"
        >
          Estadísticas generales
        </h3>
        <p className="text-xs text-blue-800/75">Según filtros de fechas y célula activos</p>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map(({ key, label, value, Icon, accent, iconClass }) => (
          <div
            key={key}
            className={`flex flex-col gap-2 rounded-lg border px-3 py-3 shadow-sm transition-shadow hover:shadow-md ${accent}`}
          >
            <div className="flex items-start gap-2">
              <span className={`rounded-md bg-blue-50 p-1.5 ${iconClass}`}>
                <Icon className="h-4 w-4 shrink-0" aria-hidden />
              </span>
              <p className="min-w-0 flex-1 text-[11px] font-medium uppercase leading-snug tracking-wide text-gray-600">
                {label}
              </p>
            </div>
            <p className="text-2xl font-bold tabular-nums text-blue-950 sm:text-[1.65rem]">{value}</p>
          </div>
        ))}
      </div>
      {!loading && stats.cantidadReportes === 0 ? (
        <p className="mt-3 text-xs text-blue-900/70">
          Sin reportes en el rango seleccionado; los promedios se muestran en 0.00.
        </p>
      ) : null}
    </section>
  );
}

async function downloadQuickStatsHistoryExport(api, kind, cellId, dateFrom, dateTo, onError) {
  const path =
    kind === "excel"
      ? "/cell-quick-weekly-stats/history/export/excel"
      : "/cell-quick-weekly-stats/history/export/pdf";
  try {
    const res = await api.get(path, {
      params: { cell_id: cellId, date_from: dateFrom, date_to: dateTo },
      responseType: "blob",
      timeout: 180_000,
    });
    const mime =
      res.headers["content-type"] ||
      (kind === "excel"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf");
    const blob = new Blob([res.data], { type: mime });
    const cd = res.headers["content-disposition"] || "";
    const m = /filename="?([^";]+)"?/i.exec(cd);
    const fallback = `historial_celulas_${dateFrom}_${dateTo}.${kind === "excel" ? "xlsx" : "pdf"}`;
    const filename = (m && m[1] ? m[1].trim() : null) || fallback;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    let mapped = err;
    if (err.response?.data instanceof Blob) {
      try {
        const t = await err.response.data.text();
        const j = JSON.parse(t);
        mapped = { ...err, response: { ...err.response, data: j } };
      } catch {
        /* keep original err */
      }
    }
    onError(mapped);
    throw mapped;
  }
}

async function downloadQuickStatsExport(api, kind, dateFrom, dateTo, onError) {
  const path = kind === "excel" ? "/cell-quick-weekly-stats/export/excel" : "/cell-quick-weekly-stats/export/pdf";
  try {
    const res = await api.get(path, {
      params: { date_from: dateFrom, date_to: dateTo },
      responseType: "blob",
      timeout: 180_000,
    });
    const mime =
      res.headers["content-type"] ||
      (kind === "excel"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf");
    const blob = new Blob([res.data], { type: mime });
    const cd = res.headers["content-disposition"] || "";
    const m = /filename="?([^";]+)"?/i.exec(cd);
    const fallback = `estadistica_rapida_${dateFrom}_${dateTo}.${kind === "excel" ? "xlsx" : "pdf"}`;
    const filename = (m && m[1] ? m[1].trim() : null) || fallback;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    let mapped = err;
    if (err.response?.data instanceof Blob) {
      try {
        const t = await err.response.data.text();
        const j = JSON.parse(t);
        mapped = { ...err, response: { ...err.response, data: j } };
      } catch {
        /* keep original err */
      }
    }
    onError?.(mapped);
  }
}

const chartBlue = "#1d4ed8";
const chartSoft = "#93c5fd";
const chartMiembros = "#0ea5e9";
const chartVisReg = "#16a34a";
const chartVisEsp = "#ea580c";
const chartInv1m = "#6366f1";

/** Evita spinner infinito si el backend tarda o no responde (axios sin timeout = pendiente eterno). */
const QUICK_STATS_DASHBOARD_TIMEOUT_MS = 120_000;
const QUICK_STATS_ALERTS_TIMEOUT_MS = 60_000;

/** Valor del selector «Todas las células» en historial y gráfica (pastor; coincide con backend). */
const ALL_CELLS = "all";

/** Agregado en gráfica «Historial por célula» para supervisor (propia + hijas + descendientes). */
const ALL_SUPERVISOR_CELLS = "ALL_SUPERVISOR_CELLS";

const normCellId = (id) => String(id ?? "").trim();

const isPerCellAggregateId = (id) => {
  const x = normCellId(id);
  return x === ALL_CELLS || x === ALL_SUPERVISOR_CELLS;
};

/** Célula con al menos un punto en la serie del dashboard. */
function perCellHasChartSeries(row) {
  return (row?.weeks_reported ?? 0) > 0 && Array.isArray(row?.series) && row.series.length > 0;
}

/** Suma reportes de varias filas `per_cell` por fecha de reunión. */
function aggregatePerCellSeries(perCellRows) {
  const rows = Array.isArray(perCellRows) ? perCellRows : [];
  const map = new Map();
  for (const row of rows) {
    const series = Array.isArray(row?.series) ? row.series : [];
    for (const p of series) {
      const fecha = String(p?.meeting_date || "").trim();
      if (!fecha) continue;
      if (!map.has(fecha)) {
        map.set(fecha, {
          fecha,
          miembros_presentes: 0,
          visitas_regulares: 0,
          visitas_esporadicas: 0,
          invitados_primer_mes: 0,
          total_asistentes: 0,
        });
      }
      const x = map.get(fecha);
      x.miembros_presentes += Number(p.members_present) || 0;
      x.visitas_regulares += Number(p.regular_visits) || 0;
      x.visitas_esporadicas += Number(p.sporadic_visits) || 0;
      x.invitados_primer_mes += Number(p.first_month_guests) || 0;
      x.total_asistentes += Number(p.total_attendees) || 0;
    }
  }
  return [...map.values()].sort((a, b) => a.fecha.localeCompare(b.fecha, "es"));
}

function aggregatePerCellHasChartSeries(perCellRows) {
  return aggregatePerCellSeries(perCellRows).length > 0;
}

/** Fila sintética para la tarjeta cuando se muestran todas las células del alcance. */
function buildSyntheticAggregateRow(perCellRows, label, cellId) {
  const chartPoints = aggregatePerCellSeries(perCellRows);
  const series = chartPoints.map((p) => ({
    meeting_date: p.fecha,
    members_present: p.miembros_presentes,
    regular_visits: p.visitas_regulares,
    sporadic_visits: p.visitas_esporadicas,
    first_month_guests: p.invitados_primer_mes,
    total_attendees: p.total_asistentes,
  }));
  const weeks_reported = series.length;
  let average_total = 0;
  let growth_pct = null;
  let trend = "insufficient_data";
  let last_total = null;
  let sum_first_month_guests = 0;
  if (weeks_reported > 0) {
    const sumTot = series.reduce((s, p) => s + (p.total_attendees || 0), 0);
    average_total = Math.round((sumTot / weeks_reported) * 10) / 10;
    sum_first_month_guests = series.reduce((s, p) => s + (p.first_month_guests || 0), 0);
    if (weeks_reported >= 2) {
      const first = series[0].total_attendees;
      const last = series[series.length - 1].total_attendees;
      last_total = last;
      growth_pct = Math.round(((last - first) / Math.max(first, 1)) * 1000) / 10;
      if (growth_pct > 5) trend = "growing";
      else if (growth_pct < -5) trend = "declining";
      else trend = "stagnant";
    } else {
      last_total = series[0].total_attendees;
    }
  }
  return {
    cell_id: cellId,
    cell_name: label,
    series,
    weeks_reported,
    average_total,
    growth_pct,
    trend,
    sum_first_month_guests,
    last_total,
  };
}

function resolvePerCellChartRow(perCell, chartId, { isPastor, isSupervisor }) {
  const list = Array.isArray(perCell) ? perCell : [];
  if (!list.length) return null;
  const id = normCellId(chartId) || normCellId(list[0]?.cell_id);
  if (isSupervisor && id === ALL_SUPERVISOR_CELLS) {
    return buildSyntheticAggregateRow(list, "Todas mis células", ALL_SUPERVISOR_CELLS);
  }
  if (isPastor && id === ALL_CELLS) {
    return buildSyntheticAggregateRow(list, "Todas las células", ALL_CELLS);
  }
  return list.find((r) => normCellId(r.cell_id) === id) || list[0] || null;
}

function defaultPerCellChartId(perCell, { isPastor, isSupervisor }) {
  const list = Array.isArray(perCell) ? perCell : [];
  if (!list.length) return "";
  if (isSupervisor) return ALL_SUPERVISOR_CELLS;
  if (isPastor) return ALL_CELLS;
  return normCellId(list[0]?.cell_id);
}

function perCellSelectionHasChartSeries(chartId, perCell) {
  const id = normCellId(chartId);
  if (isPerCellAggregateId(id)) return aggregatePerCellHasChartSeries(perCell);
  const row = (Array.isArray(perCell) ? perCell : []).find((r) => normCellId(r.cell_id) === id);
  return perCellHasChartSeries(row);
}

const PER_CELL_CHART_REMOUNT_MS = 220;

/** Tamaño fijo del contenedor (sin ResponsiveContainer: evita insertBefore con React 19 + Recharts). */
function useChartBoxSize(resetKey) {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const update = () => {
      const { width, height } = node.getBoundingClientRect();
      const w = Math.floor(width);
      const h = Math.floor(height);
      if (w > 0 && h > 0) {
        setSize((prev) => (prev.width === w && prev.height === h ? prev : { width: w, height: h }));
      }
    };

    update();
    const ro = new ResizeObserver(() => requestAnimationFrame(update));
    ro.observe(node);
    return () => ro.disconnect();
  }, [resetKey]);

  return { containerRef, width: size.width, height: size.height };
}

function seriesFromPerCellRow(row) {
  if (!row) return [];
  const series = Array.isArray(row.series) ? row.series : [];
  return series.map((p) => ({
    fecha: p.meeting_date,
    miembros_presentes: p.members_present ?? 0,
    visitas_regulares: p.regular_visits ?? 0,
    visitas_esporadicas: p.sporadic_visits ?? 0,
    invitados_primer_mes: p.first_month_guests ?? 0,
    total_asistentes: p.total_attendees ?? 0,
  }));
}

/** Tooltip completo para «Historial por célula» (coincide con campos del API). */
function CellHistoryByCellTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="min-w-[220px] rounded-lg border border-blue-200 bg-white px-3 py-2.5 text-sm shadow-md">
      <p className="mb-2 border-b border-blue-100 pb-1 font-semibold text-blue-950">Fecha: {d.fecha}</p>
      <ul className="space-y-1 text-gray-800">
        <li className="flex justify-between gap-4">
          <span>Miembros presentes</span>
          <strong className="tabular-nums text-gray-900">{d.miembros_presentes}</strong>
        </li>
        <li className="flex justify-between gap-4">
          <span>Visitas regulares</span>
          <strong className="tabular-nums text-gray-900">{d.visitas_regulares}</strong>
        </li>
        <li className="flex justify-between gap-4">
          <span>Visitas esporádicas</span>
          <strong className="tabular-nums text-gray-900">{d.visitas_esporadicas}</strong>
        </li>
        <li className="flex justify-between gap-4">
          <span>Invitados 1er mes</span>
          <strong className="tabular-nums text-gray-900">{d.invitados_primer_mes}</strong>
        </li>
        <li className="mt-1 flex justify-between gap-4 border-t border-blue-100 pt-1 font-medium text-blue-950">
          <span>Total asistentes</span>
          <strong className="tabular-nums">{d.total_asistentes}</strong>
        </li>
      </ul>
    </div>
  );
}

/** Gráfica «Historial por célula» con tamaño fijo (sin ResponsiveContainer). */
function PerCellHistoryLineChart({ cellId, data, mountKey }) {
  const chartData = Array.isArray(data) ? data : [];
  const cellKey = normCellId(cellId) || "default";
  const sizeKey = `${cellKey}-${mountKey}`;
  const { containerRef, width, height } = useChartBoxSize(sizeKey);

  if (chartData.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className="h-[300px] w-full min-h-[300px] sm:h-[360px]"
      aria-hidden={width <= 0 || height <= 0}
    >
      {width > 0 && height > 0 ? (
        <LineChart
          width={width}
          height={height}
          data={chartData}
          margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="fecha" tick={{ fontSize: 11 }} stroke="#64748b" />
          <YAxis tick={{ fontSize: 11 }} stroke="#64748b" allowDecimals={false} width={36} />
          <Tooltip content={CellHistoryByCellTooltip} isAnimationActive={false} />
          <Line
            type="monotone"
            dataKey="total_asistentes"
            name="Total asistentes"
            stroke={chartBlue}
            strokeWidth={2.5}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="miembros_presentes"
            name="Miembros presentes"
            stroke={chartMiembros}
            strokeWidth={1.8}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="visitas_regulares"
            name="Visitas regulares"
            stroke={chartVisReg}
            strokeWidth={1.8}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="visitas_esporadicas"
            name="Visitas esporádicas"
            stroke={chartVisEsp}
            strokeWidth={1.8}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="invitados_primer_mes"
            name="Invitados 1er mes"
            stroke={chartInv1m}
            strokeWidth={1.8}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      ) : null}
    </div>
  );
}

const PerCellHistoryCard = memo(function PerCellHistoryCard({
  row,
  displayedCellId,
  displayedSeries,
  isChartLoading,
  chartMountKey,
  historialCelulaChartRef,
}) {
  const trend = trendLabel(row?.trend);
  const TrendIcon = trend.Icon;
  const chartData = Array.isArray(displayedSeries) ? displayedSeries : [];
  const showChart = !isChartLoading && Boolean(normCellId(displayedCellId));

  return (
    <>
      <div key={`hdr-${normCellId(row?.cell_id)}`} className="flex flex-wrap items-center gap-3">
        <h3 className="text-lg font-semibold text-blue-950">{row?.cell_name}</h3>
        <Badge className={`gap-1 ${trend.className}`}>
          <TrendIcon className="h-3 w-3" />
          {trend.text}
        </Badge>
        {row.growth_pct != null && (
          <span className="text-sm text-gray-600">
            Crecimiento en el periodo:{" "}
            <strong>
              {row.growth_pct > 0 ? "+" : ""}
              {row.growth_pct}%
            </strong>
          </span>
        )}
        <span className="text-sm text-gray-600">
          Promedio: <strong>{row.average_total}</strong> · Inv. 1.er mes:{" "}
          <strong>{row.sum_first_month_guests}</strong>
        </span>
      </div>
      <div className="h-[300px] w-full sm:h-[360px]">
        {isChartLoading ? (
          <div className="flex h-full items-center justify-center rounded-lg border border-blue-100 bg-blue-50/40 p-6">
            <p className="text-sm text-gray-600">Cargando gráfica…</p>
          </div>
        ) : showChart && chartData.length > 0 ? (
          <div
            ref={historialCelulaChartRef}
            key={`per-cell-chart-host-${chartMountKey}-${normCellId(displayedCellId)}`}
            className="h-full min-h-[280px] w-full rounded-lg bg-white"
            data-historial-celula-chart-export
          >
            <PerCellHistoryLineChart
              cellId={displayedCellId}
              data={chartData}
              mountKey={chartMountKey}
            />
          </div>
        ) : !isChartLoading ? (
          <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-blue-200 bg-blue-50/50 p-6">
            <p className="text-center text-sm text-gray-600">
              Esta célula no tiene reportes en el rango seleccionado.
            </p>
          </div>
        ) : null}
      </div>
    </>
  );
});

/**
 * Reporte estadístico rápido por célula + dashboard de crecimiento.
 * @param {import("axios").AxiosInstance} api
 * @param {Array<{ _id?: string, id?: string, name: string }>} cells
 * @param {(err: unknown) => string} formatApiError
 * @param {boolean} hasFullAccess pastor (comparación global en cabecera)
 * @param {string} [userRole] pastor | supervisor | lider
 */
export function CellQuickWeeklyStatsScreen({
  api,
  cells = [],
  formatApiError,
  hasFullAccess = false,
  userRole = "",
}) {
  const role = String(userRole || "").toLowerCase();
  const isPastorRole = hasFullAccess || role === "pastor" || role === "administrador";
  const isSupervisorRole = role === "supervisor";
  const [mainTab, setMainTab] = useState("report");

  const cellIdKey = (c) => c.id || c._id;

  const [formCell, setFormCell] = useState("");
  const [formDate, setFormDate] = useState(() => todayIso());
  const [membersPresent, setMembersPresent] = useState("0");
  const [regularVisits, setRegularVisits] = useState("0");
  const [sporadicVisits, setSporadicVisits] = useState("0");
  const [firstMonthGuests, setFirstMonthGuests] = useState("0");
  const [meetingSuspended, setMeetingSuspended] = useState("no");
  const [suspensionReason, setSuspensionReason] = useState("");
  const [planningMeetingHeld, setPlanningMeetingHeld] = useState("no");
  const [formMsg, setFormMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingSlot, setLoadingSlot] = useState(false);

  const [reportAlerts, setReportAlerts] = useState([]);
  const [exporting, setExporting] = useState(null);
  const [historyExporting, setHistoryExporting] = useState(null);
  const historyChartExportRef = useRef(null);
  const historialCelulaChartRef = useRef(null);
  const [perCellChartExporting, setPerCellChartExporting] = useState(false);
  const [perCellChartExportMsg, setPerCellChartExportMsg] = useState(null);

  const [historyCellFilter, setHistoryCellFilter] = useState(ALL_CELLS);
  const [histFrom, setHistFrom] = useState(() => defaultHistoryFrom());
  const [histTo, setHistTo] = useState(() => todayIso());
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyErr, setHistoryErr] = useState("");

  const loadReportAlerts = useCallback(async () => {
    try {
      const res = await api.get("/cell-quick-weekly-stats/report-alerts", {
        timeout: QUICK_STATS_ALERTS_TIMEOUT_MS,
      });
      setReportAlerts(res.data?.alerts || []);
    } catch (err) {
      console.error("[ICJEME] report-alerts:", err, err.response?.status);
      setReportAlerts([]);
    }
  }, [api]);

  useEffect(() => {
    loadReportAlerts();
  }, [loadReportAlerts]);

  useEffect(() => {
    if (cells.length > 0 && !formCell) {
      setFormCell(cellIdKey(cells[0]));
    }
  }, [cells, formCell]);

  const mp = parseNonNegInt(membersPresent);
  const rv = parseNonNegInt(regularVisits);
  const sv = parseNonNegInt(sporadicVisits);
  const fg = parseNonNegInt(firstMonthGuests);
  const totalAsistentes = mp + rv + sv + fg;
  const reunionSuspendida = meetingSuspended === "yes";

  const resetNumericForm = () => {
    setMembersPresent("0");
    setRegularVisits("0");
    setSporadicVisits("0");
    setFirstMonthGuests("0");
  };

  const loadSlot = useCallback(async () => {
    if (!formCell || formCell === ALL_CELLS || !formDate) return;
    setLoadingSlot(true);
    try {
      const res = await api.get("/cell-quick-weekly-stats", {
        params: { cell_id: formCell, date_from: formDate, date_to: formDate },
      });
      const row = (res.data || [])[0];
      if (row) {
        setMembersPresent(String(row.members_present ?? 0));
        setRegularVisits(String(row.regular_visits ?? 0));
        setSporadicVisits(String(row.sporadic_visits ?? 0));
        setFirstMonthGuests(String(row.first_month_guests ?? 0));
        setMeetingSuspended(toYesNoChoice(Boolean(row.meeting_suspended)));
        setSuspensionReason(String(row.suspension_reason || "").trim());
        setPlanningMeetingHeld(toYesNoChoice(Boolean(row.planning_meeting_held)));
      } else {
        resetNumericForm();
        setMeetingSuspended("no");
        setSuspensionReason("");
        setPlanningMeetingHeld("no");
      }
    } catch {
      resetNumericForm();
      setMeetingSuspended("no");
      setSuspensionReason("");
      setPlanningMeetingHeld("no");
    } finally {
      setLoadingSlot(false);
    }
  }, [api, formCell, formDate]);

  useEffect(() => {
    loadSlot();
  }, [loadSlot]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryErr("");
    try {
      const res = await api.get("/cell-quick-weekly-stats", {
        params: {
          cell_id: historyCellFilter,
          date_from: histFrom,
          date_to: histTo,
        },
      });
      const rows = [...(res.data || [])].sort((a, b) => {
        const db = String(b.meeting_date || "");
        const da = String(a.meeting_date || "");
        const c = db.localeCompare(da, "es");
        if (c !== 0) return c;
        const na = cells.find((x) => String(cellIdKey(x)) === String(a.cell_id))?.name || String(a.cell_id || "");
        const nb = cells.find((x) => String(cellIdKey(x)) === String(b.cell_id))?.name || String(b.cell_id || "");
        return na.localeCompare(nb, "es");
      });
      setHistoryRows(rows);
    } catch (err) {
      setHistoryErr(formatApiError(err));
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [api, historyCellFilter, histFrom, histTo, formatApiError, cells]);

  useEffect(() => {
    if (mainTab === "history") loadHistory();
  }, [mainTab, loadHistory]);

  const onNumberChange = (setter) => (e) => {
    const v = e.target.value;
    if (v === "") {
      setter("");
      return;
    }
    if (!/^\d+$/.test(v)) return;
    setter(v);
  };

  const onBlurFillZero = (setter, val) => () => {
    if (val === "" || val == null) setter("0");
  };

  const handleSaveReport = async () => {
    if (!formCell || formCell === ALL_CELLS || !formDate) {
      setFormMsg({ type: "err", text: "Seleccione una célula concreta (no «Todas las células») y la fecha." });
      return;
    }
    if (reunionSuspendida && !suspensionReason.trim()) {
      setFormMsg({ type: "err", text: "Indique el motivo de suspensión de la reunión." });
      return;
    }
    setSaving(true);
    setFormMsg(null);
    try {
      await api.put("/cell-quick-weekly-stats", {
        cell_id: formCell,
        meeting_date: formDate,
        members_present: mp,
        regular_visits: rv,
        sporadic_visits: sv,
        first_month_guests: fg,
        meeting_suspended: yesNoToBool(meetingSuspended),
        suspension_reason: reunionSuspendida ? suspensionReason.trim() : "",
        planning_meeting_held: yesNoToBool(planningMeetingHeld),
      });
      setFormMsg({ type: "ok", text: "Reporte guardado. Puede continuar con otra fecha o ver las gráficas." });
      await loadSlot();
      await loadHistory();
      await loadReportAlerts();
    } catch (err) {
      setFormMsg({ type: "err", text: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const [dashFrom, setDashFrom] = useState(() => defaultDashFrom());
  const [dashTo, setDashTo] = useState(() => todayIso());
  const [dash, setDash] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);
  const [dashErr, setDashErr] = useState("");
  const [chartCellId, setChartCellId] = useState("");
  /** ID de célula cuya serie está montada en Recharts (puede ir detrás del selector durante la carga). */
  const [displayedCellId, setDisplayedCellId] = useState("");
  const [perCellChartLoading, setPerCellChartLoading] = useState(false);
  const [perCellChartMountKey, setPerCellChartMountKey] = useState(0);
  const perCellChartRemountTimerRef = useRef(null);

  /** Evita carreras: solo la petición más reciente puede bajar `dashLoading` y aplicar datos. */
  const dashboardReqSeq = useRef(0);
  const dashboardAbortRef = useRef(null);

  useEffect(() => {
    return () => {
      dashboardAbortRef.current?.abort();
      dashboardReqSeq.current += 1;
    };
  }, []);

  const loadDashboard = useCallback(async () => {
    dashboardAbortRef.current?.abort();
    const aborter = new AbortController();
    dashboardAbortRef.current = aborter;
    const seq = ++dashboardReqSeq.current;

    setDashLoading(true);
    setDashErr("");
    try {
      const res = await api.get("/cell-quick-weekly-stats/dashboard", {
        params: { date_from: dashFrom, date_to: dashTo },
        timeout: QUICK_STATS_DASHBOARD_TIMEOUT_MS,
        signal: aborter.signal,
      });
      if (seq !== dashboardReqSeq.current) return;
      const payload = res.data ?? null;
      if (!payload || typeof payload !== "object") {
        console.error("[ICJEME] Error estadísticas: respuesta vacía o inválida", res?.status);
        setDash(null);
        setDashErr("No se pudieron cargar las estadísticas.");
        return;
      }
      setDash(payload);
      const list = Array.isArray(payload?.per_cell) ? payload.per_cell : [];
      const roleOpts = { isPastor: isPastorRole, isSupervisor: isSupervisorRole };
      setChartCellId((prev) => {
        const p = normCellId(prev);
        const aggregateOk =
          (isSupervisorRole && p === ALL_SUPERVISOR_CELLS) || (isPastorRole && p === ALL_CELLS);
        const inList = p && list.some((r) => normCellId(r.cell_id) === p);
        const id = aggregateOk || inList ? p : defaultPerCellChartId(list, roleOpts);
        setDisplayedCellId(id);
        setPerCellChartLoading(false);
        setPerCellChartMountKey((k) => k + 1);
        return id;
      });
    } catch (err) {
      const canceled =
        aborter.signal.aborted || err?.code === "ERR_CANCELED" || err?.name === "CanceledError";
      if (canceled || seq !== dashboardReqSeq.current) return;
      console.error("[ICJEME] Error estadísticas:", err, err?.response?.status);
      setDash(null);
      setDashErr(formatApiError(err) || "No se pudieron cargar las estadísticas.");
    } finally {
      if (seq === dashboardReqSeq.current) {
        setDashLoading(false);
      }
    }
    void loadReportAlerts().catch((e) =>
      console.error("[ICJEME] report-alerts (tras dashboard):", e, e?.response?.status)
    );
  }, [api, dashFrom, dashTo, formatApiError, loadReportAlerts]);

  useEffect(() => {
    if (mainTab !== "analytics") {
      dashboardAbortRef.current?.abort();
      dashboardReqSeq.current += 1;
      setDashLoading(false);
      if (perCellChartRemountTimerRef.current) {
        clearTimeout(perCellChartRemountTimerRef.current);
        perCellChartRemountTimerRef.current = null;
      }
      setPerCellChartLoading(false);
      return;
    }
    loadDashboard();
  }, [mainTab, loadDashboard]);

  const perCellChartRow = useMemo(
    () =>
      resolvePerCellChartRow(dash?.per_cell, chartCellId, {
        isPastor: isPastorRole,
        isSupervisor: isSupervisorRole,
      }),
    [dash, chartCellId, isPastorRole, isSupervisorRole]
  );

  const displayedPerCellRow = useMemo(
    () =>
      resolvePerCellChartRow(dash?.per_cell, displayedCellId, {
        isPastor: isPastorRole,
        isSupervisor: isSupervisorRole,
      }),
    [dash, displayedCellId, isPastorRole, isSupervisorRole]
  );

  const displayedSeries = useMemo(
    () => seriesFromPerCellRow(displayedPerCellRow),
    [displayedPerCellRow]
  );

  const barData = useMemo(() => {
    if (!dash?.per_cell) return [];
    return [...dash.per_cell]
      .map((r) => ({
        nombre: r.cell_name.length > 22 ? `${r.cell_name.slice(0, 20)}…` : r.cell_name,
        ultimo: r.last_total ?? 0,
        promedio: r.average_total,
      }))
      .sort((a, b) => b.ultimo - a.ultimo)
      .slice(0, 18);
  }, [dash]);

  const globalLine = useMemo(() => {
    if (!dash?.global_by_week) return [];
    return dash.global_by_week.map((g) => ({
      fecha: g.meeting_date,
      asistentes: g.total_attendees_sum,
      células: g.cells_reporting,
    }));
  }, [dash]);

  const monthlyLine = useMemo(() => {
    if (!dash?.monthly_trend) return [];
    return dash.monthly_trend.map((m) => ({
      mes: m.month,
      asistentes: m.total_attendees_sum,
    }));
  }, [dash]);

  const historyTotals = useMemo(() => {
    let miembros = 0;
    let visitasReg = 0;
    let visitasEsp = 0;
    let invNuevos = 0;
    let totalAsist = 0;
    for (const r of historyRows) {
      miembros += Number(r.members_present) || 0;
      visitasReg += Number(r.regular_visits) || 0;
      visitasEsp += Number(r.sporadic_visits) || 0;
      invNuevos += Number(r.first_month_guests) || 0;
      totalAsist += Number(r.total_attendees) || 0;
    }
    return {
      miembros,
      visitasReg,
      visitasEsp,
      visitas: visitasReg + visitasEsp,
      invNuevos,
      totalAsist,
    };
  }, [historyRows]);

  const historyGeneralStats = useMemo(() => computeHistoryGeneralStats(historyRows), [historyRows]);

  /** Por fecha de reunión: suma de todas las filas del historial (una o muchas células). */
  const historyChartData = useMemo(() => {
    const map = new Map();
    for (const r of historyRows) {
      const d = String(r.meeting_date || "");
      if (!d) continue;
      if (!map.has(d)) {
        map.set(d, {
          fecha: d,
          total_asistentes: 0,
          miembros_presentes: 0,
          visitas_regulares: 0,
          visitas_esporadicas: 0,
          invitados_primer_mes: 0,
        });
      }
      const x = map.get(d);
      x.total_asistentes += Number(r.total_attendees) || 0;
      x.miembros_presentes += Number(r.members_present) || 0;
      x.visitas_regulares += Number(r.regular_visits) || 0;
      x.visitas_esporadicas += Number(r.sporadic_visits) || 0;
      x.invitados_primer_mes += Number(r.first_month_guests) || 0;
    }
    return [...map.values()].sort((a, b) => a.fecha.localeCompare(b.fecha, "es"));
  }, [historyRows]);

  const resolveHistoryCellName = useCallback(
    (cid) => cells.find((c) => String(cellIdKey(c)) === String(cid))?.name || String(cid || "—"),
    [cells]
  );

  const handlePerCellChartSelect = useCallback(
    (value) => {
      const next = normCellId(value);
      if (!next || next === normCellId(chartCellId)) return;

      if (perCellChartRemountTimerRef.current) {
        clearTimeout(perCellChartRemountTimerRef.current);
        perCellChartRemountTimerRef.current = null;
      }

      setPerCellChartExportMsg(null);
      setPerCellChartLoading(true);
      setDisplayedCellId("");
      setChartCellId(next);

      const perCell = Array.isArray(dash?.per_cell) ? dash.per_cell : [];
      const hasSeries = perCellSelectionHasChartSeries(next, perCell);

      const finish = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setDisplayedCellId(next);
            setPerCellChartMountKey((k) => k + 1);
            setPerCellChartLoading(false);
            perCellChartRemountTimerRef.current = null;
          });
        });
      };

      if (!hasSeries) {
        finish();
        return;
      }

      perCellChartRemountTimerRef.current = window.setTimeout(finish, PER_CELL_CHART_REMOUNT_MS);
    },
    [chartCellId, dash]
  );

  /** Mismo array que alimenta el LineChart visible (`displayedSeries`). */
  const perCellChartExportData = displayedSeries;

  const handleExportPerCellChartPdf = useCallback(async () => {
    setPerCellChartExportMsg(null);

    const dataParaExportar = Array.isArray(perCellChartExportData) ? perCellChartExportData : [];
    if (perCellChartLoading) {
      setPerCellChartExportMsg({
        type: "err",
        text: "Espere a que termine de cargar la gráfica.",
      });
      return;
    }
    if (dataParaExportar.length === 0) {
      setPerCellChartExportMsg({
        type: "err",
        text: "No hay datos para exportar esta gráfica.",
      });
      return;
    }

    if (!historialCelulaChartRef.current?.isConnected) {
      setPerCellChartExportMsg({
        type: "err",
        text: "No hay datos para exportar esta gráfica.",
      });
      return;
    }

    const exportRow =
      displayedPerCellRow ||
      resolvePerCellChartRow(dash?.per_cell, displayedCellId, {
        isPastor: isPastorRole,
        isSupervisor: isSupervisorRole,
      });

    setPerCellChartExporting(true);
    try {
      const result = await exportPerCellHistoryChartPdf({
        chartRef: historialCelulaChartRef,
        chartData: dataParaExportar,
        selectionLabel: exportRow?.cell_name || "",
        dateFrom: dashFrom,
        dateTo: dashTo,
        growthPct: exportRow?.growth_pct,
        averageTotal: exportRow?.average_total,
        sumFirstMonthGuests: exportRow?.sum_first_month_guests,
        chartBusy: perCellChartLoading,
      });
      if (!result.ok) {
        setPerCellChartExportMsg({
          type: "err",
          text: result.message || "No hay datos para exportar esta gráfica.",
        });
      }
    } catch (err) {
      console.error("[ICJEME] export per-cell chart PDF:", err);
      setPerCellChartExportMsg({
        type: "err",
        text: "No se pudo generar el PDF de la gráfica.",
      });
    } finally {
      setPerCellChartExporting(false);
    }
  }, [
    perCellChartExportData,
    perCellChartLoading,
    displayedPerCellRow,
    displayedCellId,
    dash,
    isPastorRole,
    isSupervisorRole,
    dashFrom,
    dashTo,
  ]);

  return (
    <div className="space-y-6 pb-6">
      <div className="rounded-xl border border-blue-100 bg-gradient-to-br from-white to-blue-50/80 px-4 py-5 shadow-sm sm:px-6">
        <h2 className="text-xl font-bold text-blue-950 sm:text-2xl">Estadística Rápida</h2>
        <p className="mt-1 max-w-3xl text-sm text-blue-900/80">
          Envíe totales por semana sin registrar persona por persona. Luego analice el crecimiento con gráficas por
          célula y de toda la iglesia.
        </p>
        {hasFullAccess && (
          <p className="mt-2 text-xs text-blue-800/70">Vista pastoral: comparación entre células y tendencias globales.</p>
        )}
      </div>

      {reportAlerts.length > 0 && (
        <div
          className="flex gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-950 shadow-sm"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden />
          <div className="min-w-0 text-sm">
            <p className="font-semibold text-amber-950">
              {reportAlerts.length} célula{reportAlerts.length === 1 ? "" : "s"} sin enviar reporte rápido en 14 días o más
            </p>
            <p className="mt-1 text-xs text-amber-900/90">
              Se considera la última vez que se guardó un reporte (no la fecha de reunión).
            </p>
            <ul className="mt-2 max-h-40 list-disc space-y-1 overflow-y-auto pl-5 text-sm text-amber-950/95">
              {reportAlerts.slice(0, 12).map((a) => (
                <li key={a.cell_id}>
                  <span className="font-medium">{a.cell_name}</span>
                  {a.never_submitted
                    ? " — sin ningún reporte aún"
                    : a.days_since_last_submit != null
                      ? ` — último envío hace ${a.days_since_last_submit} día${a.days_since_last_submit === 1 ? "" : "s"}`
                      : ""}
                </li>
              ))}
            </ul>
            {reportAlerts.length > 12 && (
              <p className="mt-1 text-xs text-amber-900/80">…y {reportAlerts.length - 12} más.</p>
            )}
          </div>
        </div>
      )}

      <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
        <TabsList className="grid h-auto w-full max-w-3xl grid-cols-1 gap-1 rounded-xl border border-blue-100 bg-blue-50/90 p-1 sm:grid-cols-3">
          <TabsTrigger
            value="report"
            className="gap-2 py-3 text-sm data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-md"
          >
            <ClipboardList className="h-4 w-4 shrink-0" />
            Enviar reporte
          </TabsTrigger>
          <TabsTrigger
            value="history"
            className="gap-2 py-3 text-sm data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-md"
          >
            <History className="h-4 w-4 shrink-0" />
            Historial
          </TabsTrigger>
          <TabsTrigger
            value="analytics"
            className="gap-2 py-3 text-sm data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-md"
          >
            <BarChart3 className="h-4 w-4 shrink-0" />
            Gráficas e informes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="report" className="mt-4 space-y-4">
          {formMsg && (
            <div
              className={`rounded-lg border px-4 py-3 text-sm ${
                formMsg.type === "ok"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                  : "border-red-200 bg-red-50 text-red-800"
              }`}
            >
              {formMsg.text}
            </div>
          )}

          <Card className="border-blue-100 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg text-blue-950">Reporte numérico semanal</CardTitle>
              <CardDescription>Solo números enteros. El total se calcula solo.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="q-cell">Célula</Label>
                  <NativeSelect
                    id="q-cell"
                    value={formCell}
                    onChange={setFormCell}
                    placeholder="Elija la célula"
                  >
                    {cells.map((c) => {
                      const id = cellIdKey(c);
                      return (
                        <option key={id} value={id}>
                          {c.name}
                        </option>
                      );
                    })}
                  </NativeSelect>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="q-date">Fecha de reunión</Label>
                  <Input
                    id="q-date"
                    type="date"
                    value={formDate}
                    onChange={(e) => setFormDate(e.target.value)}
                    className="h-11 border-blue-200 sm:h-10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="q-suspended">¿Se suspendió la reunión?</Label>
                  <NativeSelect
                    id="q-suspended"
                    value={meetingSuspended}
                    onChange={(v) => {
                      setMeetingSuspended(v);
                      if (v !== "yes") setSuspensionReason("");
                    }}
                  >
                    <option value="no">No</option>
                    <option value="yes">Sí</option>
                  </NativeSelect>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="q-planning">¿Hubo reunión de planificación?</Label>
                  <NativeSelect id="q-planning" value={planningMeetingHeld} onChange={setPlanningMeetingHeld}>
                    <option value="no">No</option>
                    <option value="yes">Sí</option>
                  </NativeSelect>
                </div>
              </div>

              {reunionSuspendida && (
                <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/80 px-4 py-3">
                  <Label htmlFor="q-suspension-reason">Motivo de suspensión *</Label>
                  <Textarea
                    id="q-suspension-reason"
                    value={suspensionReason}
                    onChange={(e) => setSuspensionReason(e.target.value)}
                    rows={3}
                    className="border-amber-200 bg-white"
                    placeholder="Indique por qué no se realizó la reunión"
                  />
                  <p className="text-xs text-amber-900/90">
                    Puede guardar el reporte con asistencia en 0. No es obligatorio registrar asistentes.
                  </p>
                </div>
              )}

              <div
                className={`rounded-xl border-2 px-4 py-4 text-center shadow-inner ${
                  reunionSuspendida
                    ? "border-amber-500 bg-amber-600 text-white"
                    : "border-blue-600 bg-blue-600 text-white"
                }`}
              >
                <p className="text-xs font-medium uppercase tracking-wider opacity-90">Total de asistentes</p>
                <p className="text-4xl font-bold tabular-nums sm:text-5xl">{totalAsistentes}</p>
                <p className="mt-1 text-xs opacity-90">
                  {reunionSuspendida
                    ? "Reunión suspendida — el total puede ser 0"
                    : "Miembros + visitas + invitados primer mes"}
                </p>
              </div>

              <div
                className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${reunionSuspendida ? "opacity-80" : ""}`}
                aria-disabled={reunionSuspendida}
              >
                {[
                  ["Miembros presentes", membersPresent, setMembersPresent],
                  ["Visitas regulares", regularVisits, setRegularVisits],
                  ["Visitas esporádicas", sporadicVisits, setSporadicVisits],
                  ["Invitados primer mes", firstMonthGuests, setFirstMonthGuests],
                ].map(([label, val, setter]) => (
                  <div key={label} className="space-y-2">
                    <Label className="text-gray-800">{label}</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="off"
                      value={val}
                      onChange={onNumberChange(setter)}
                      onBlur={onBlurFillZero(setter, val)}
                      className="h-12 border-blue-200 text-lg tabular-nums sm:h-11"
                      placeholder="0"
                      disabled={reunionSuspendida}
                    />
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-gray-500">
                  {loadingSlot ? "Cargando datos de esta fecha…" : "Si ya había reporte para esta fecha, se muestra aquí."}
                </p>
                <Button
                  type="button"
                  className="h-12 w-full bg-blue-600 text-base hover:bg-blue-700 sm:h-11 sm:w-auto sm:min-w-[200px]"
                  disabled={saving || !formCell || formCell === ALL_CELLS}
                  onClick={handleSaveReport}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? "Guardando…" : "Guardar reporte semanal"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4 space-y-4">
          <Card className="border-blue-100 shadow-md">
            <CardHeader className="print:hidden">
              <CardTitle className="text-lg text-blue-950">Historial de reportes guardados</CardTitle>
              <CardDescription>
                Elija «Todas las células» o una célula concreta y el rango de fechas de reunión. Los totales y la gráfica
                reflejan las filas mostradas en la tabla.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="print:hidden grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-2 sm:col-span-2">
                  <Label>Célula</Label>
                  <NativeSelect value={historyCellFilter} onChange={setHistoryCellFilter} placeholder="Elija">
                    <option value={ALL_CELLS}>Todas las células</option>
                    {cells.map((c) => {
                      const id = cellIdKey(c);
                      return (
                        <option key={id} value={id}>
                          {c.name}
                        </option>
                      );
                    })}
                  </NativeSelect>
                </div>
                <div className="space-y-2">
                  <Label>Desde (reunión)</Label>
                  <Input type="date" value={histFrom} onChange={(e) => setHistFrom(e.target.value)} className="border-blue-200" />
                </div>
                <div className="space-y-2">
                  <Label>Hasta (reunión)</Label>
                  <Input type="date" value={histTo} onChange={(e) => setHistTo(e.target.value)} className="border-blue-200" />
                </div>
              </div>
              <div className="print:hidden flex flex-wrap gap-2">
                <Button type="button" variant="secondary" className="border border-blue-200 bg-white" onClick={loadHistory} disabled={historyLoading}>
                  {historyLoading ? "Cargando…" : "Actualizar historial"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2 border-blue-300 bg-white text-blue-800 hover:bg-blue-50"
                  disabled={!!historyExporting || historyLoading}
                  onClick={async () => {
                    setHistoryExporting("excel");
                    setHistoryErr("");
                    try {
                      await downloadQuickStatsHistoryExport(
                        api,
                        "excel",
                        historyCellFilter,
                        histFrom,
                        histTo,
                        (err) => setHistoryErr(formatApiError(err) || "No se pudo exportar el historial.")
                      );
                    } finally {
                      setHistoryExporting(null);
                    }
                  }}
                  {...excelExportButtonA11y}
                >
                  <FileSpreadsheet className="h-4 w-4 shrink-0" aria-hidden />
                  <ExcelExportLabel busy={historyExporting === "excel"} />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="border-blue-300 bg-white text-blue-800 hover:bg-blue-50"
                  disabled={!!historyExporting || historyLoading}
                  onClick={async () => {
                    setHistoryExporting("pdf");
                    setHistoryErr("");
                    try {
                      await exportQuickStatsHistoryPdf({
                        api,
                        cellId: historyCellFilter,
                        dateFrom: histFrom,
                        dateTo: histTo,
                        chartRef: historyChartExportRef,
                        hasChartData: historyChartData.length > 0,
                        onError: (err) =>
                          setHistoryErr(formatApiError(err) || "No se pudo exportar el historial."),
                      });
                    } finally {
                      setHistoryExporting(null);
                    }
                  }}
                >
                  <FileDown className="mr-2 h-4 w-4 shrink-0" />
                  {historyExporting === "pdf" ? "Generando…" : "PDF"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="gap-1 border-blue-300 bg-white text-blue-800 hover:bg-blue-50"
                  disabled={historyLoading}
                  onClick={() => window.print()}
                >
                  <Printer className="h-4 w-4 shrink-0" />
                  Imprimir
                </Button>
              </div>

              <div id="quick-stats-history-print" className="space-y-4">
              <HistoryGeneralStatsPanel stats={historyGeneralStats} loading={historyLoading} />

              {historyRows.length > 0 && (
                <div className="grid gap-2 rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <p className="text-xs font-medium uppercase text-blue-900/80">Total asistentes (suma)</p>
                    <p className="text-lg font-bold tabular-nums text-blue-950">{historyTotals.totalAsist}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase text-blue-900/80">Total visitas (reg. + esp.)</p>
                    <p className="text-lg font-bold tabular-nums text-blue-950">{historyTotals.visitas}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase text-blue-900/80">Total nuevos (inv. 1er mes)</p>
                    <p className="text-lg font-bold tabular-nums text-blue-950">{historyTotals.invNuevos}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase text-blue-900/80">Total miembros presentes</p>
                    <p className="text-lg font-bold tabular-nums text-blue-950">{historyTotals.miembros}</p>
                  </div>
                </div>
              )}
              {historyErr && (
                <div className="print:hidden rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{historyErr}</div>
              )}
              <div className="overflow-x-auto rounded-lg border border-blue-100 bg-white print:overflow-visible print:border-gray-400">
                <table className="quick-stats-history-table w-full min-w-[1100px] text-sm print:min-w-0 print:text-xs">
                  <thead className="border-b border-blue-100 bg-blue-50/80 print:bg-gray-800 print:text-white">
                    <tr className="text-left text-xs font-semibold uppercase tracking-wide text-blue-900">
                      <th className="min-w-[9rem] p-3">Célula</th>
                      <th className="w-28 whitespace-nowrap p-3">Fecha reunión</th>
                      <th className="w-28 p-3">Susp.</th>
                      <th className="min-w-[14rem] max-w-[20rem] p-3">Motivo</th>
                      <th className="w-24 p-3">Planif.</th>
                      <th className="w-20 p-3 text-right">Miembros</th>
                      <th className="w-20 p-3 text-right">V. regular</th>
                      <th className="w-20 p-3 text-right">V. esporád.</th>
                      <th className="w-24 p-3 text-right">Inv. 1er mes</th>
                      <th className="w-16 p-3 text-right">Total</th>
                      <th className="min-w-[7rem] p-3">Enviado por</th>
                      <th className="min-w-[8rem] p-3">Última actualización</th>
                      <th className="w-20 p-3 print:hidden"> </th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyLoading ? (
                      <tr>
                        <td colSpan={13} className="p-8 text-center text-gray-500">
                          Cargando…
                        </td>
                      </tr>
                    ) : historyRows.length === 0 ? (
                      <tr>
                        <td colSpan={13} className="p-8 text-center text-gray-500">
                          No hay reportes en este rango para el filtro seleccionado.
                        </td>
                      </tr>
                    ) : (
                      historyRows.map((row) => {
                        const rid = row.id || row._id || `${row.cell_id}-${row.meeting_date}`;
                        const upd = row.updated_at || row.created_at;
                        const updStr = upd ? new Date(upd).toLocaleString("es") : "—";
                        const suspended = isQuickStatMeetingSuspended(row);
                        const motiveText = suspended ? String(row.suspension_reason || "").trim() : "";
                        const planningHeld = Boolean(row.planning_meeting_held ?? row.planningMeetingHeld);
                        return (
                          <tr
                            key={rid}
                            className={`border-b border-gray-100 ${
                              suspended
                                ? "quick-stats-history-row--suspended"
                                : "hover:bg-blue-50/40"
                            }`}
                          >
                            <td className="p-3 font-medium">{resolveHistoryCellName(row.cell_id)}</td>
                            <td className="whitespace-nowrap p-3">{row.meeting_date}</td>
                            <td className="p-3">
                              <QuickStatsSuspensionBadge suspended={suspended} />
                            </td>
                            <td className="max-w-[20rem] p-3 align-top">
                              {motiveText ? (
                                <span
                                  className="block truncate"
                                  title={motiveText}
                                >
                                  {motiveText}
                                </span>
                              ) : (
                                <span className="text-gray-500">—</span>
                              )}
                            </td>
                            <td className="p-3">
                              <QuickStatsPlanningLabel held={planningHeld} />
                            </td>
                            <td className="p-3 text-right tabular-nums">{row.members_present ?? 0}</td>
                            <td className="p-3 text-right tabular-nums">{row.regular_visits ?? 0}</td>
                            <td className="p-3 text-right tabular-nums">{row.sporadic_visits ?? 0}</td>
                            <td className="p-3 text-right tabular-nums">{row.first_month_guests ?? 0}</td>
                            <td className="p-3 text-right font-semibold tabular-nums text-blue-900">{row.total_attendees ?? 0}</td>
                            <td className="p-3 text-gray-700">{row.submitted_by_username || "—"}</td>
                            <td className="p-3 text-gray-600">{updStr}</td>
                            <td className="p-2 print:hidden">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="whitespace-nowrap border-blue-200 text-blue-800"
                                onClick={() => {
                                  setFormCell(String(row.cell_id || ""));
                                  setFormDate(row.meeting_date || formDate);
                                  setMeetingSuspended(toYesNoChoice(Boolean(row.meeting_suspended)));
                                  setSuspensionReason(String(row.suspension_reason || "").trim());
                                  setPlanningMeetingHeld(toYesNoChoice(Boolean(row.planning_meeting_held)));
                                  setMembersPresent(String(row.members_present ?? 0));
                                  setRegularVisits(String(row.regular_visits ?? 0));
                                  setSporadicVisits(String(row.sporadic_visits ?? 0));
                                  setFirstMonthGuests(String(row.first_month_guests ?? 0));
                                  setMainTab("report");
                                }}
                              >
                                Editar
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  {!historyLoading && historyRows.length > 0 && (
                    <tfoot className="border-t-2 border-blue-200 bg-blue-50/90">
                      <tr className="font-semibold text-blue-950">
                        <td colSpan={5} className="p-3 uppercase tracking-wide">
                          Total acumulado
                        </td>
                        <td className="p-3 text-right tabular-nums">{historyTotals.miembros}</td>
                        <td className="p-3 text-right tabular-nums">{historyTotals.visitasReg}</td>
                        <td className="p-3 text-right tabular-nums">{historyTotals.visitasEsp}</td>
                        <td className="p-3 text-right tabular-nums">{historyTotals.invNuevos}</td>
                        <td className="p-3 text-right text-lg tabular-nums text-blue-900">{historyTotals.totalAsist}</td>
                        <td colSpan={3} className="p-3 text-xs font-normal normal-case text-blue-900/80">
                          Total general (suma de columna Total) = {historyTotals.totalAsist} · Visitas (reg.+esp.) ={" "}
                          {historyTotals.visitas} · Nuevos = {historyTotals.invNuevos}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              </div>

              {mainTab === "history" && historyChartData.length > 0 && (
                <Card className="print:hidden border-blue-100">
                  <div
                    ref={historyChartExportRef}
                    className="rounded-lg bg-white"
                    data-pdf-section="history-chart"
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-blue-950">
                        Gráfica del historial (acumulado por fecha de reunión)
                      </CardTitle>
                      <CardDescription className="text-xs">
                        {historyCellFilter === ALL_CELLS
                          ? "Suma de todas las células por cada fecha. Use el filtro de fechas arriba."
                          : "Totales por fecha para la célula seleccionada."}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="h-[280px] w-full sm:h-[300px]">
                      <ResponsiveContainer
                        key={`history-rc-${historyCellFilter}`}
                        width="100%"
                        height="100%"
                        debounce={50}
                      >
                        <LineChart
                          key={`history-lc-${historyCellFilter}`}
                          data={historyChartData}
                          margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="fecha" tick={{ fontSize: 11 }} stroke="#64748b" />
                          <YAxis tick={{ fontSize: 11 }} stroke="#64748b" allowDecimals={false} width={40} />
                          <Tooltip content={CellHistoryByCellTooltip} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Line
                            type="monotone"
                            dataKey="total_asistentes"
                            name="Total asistentes"
                            stroke={chartBlue}
                            strokeWidth={2.5}
                            dot={{ r: 3 }}
                            isAnimationActive={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="miembros_presentes"
                            name="Miembros presentes"
                            stroke={chartMiembros}
                            strokeWidth={1.6}
                            dot={false}
                            isAnimationActive={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="visitas_regulares"
                            name="Visitas regulares"
                            stroke={chartVisReg}
                            strokeWidth={1.6}
                            dot={false}
                            isAnimationActive={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="visitas_esporadicas"
                            name="Visitas esporádicas"
                            stroke={chartVisEsp}
                            strokeWidth={1.6}
                            dot={false}
                            isAnimationActive={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="invitados_primer_mes"
                            name="Invitados 1er mes"
                            stroke={chartInv1m}
                            strokeWidth={1.6}
                            dot={false}
                            isAnimationActive={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </div>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="mt-4 space-y-6">
          {mainTab !== "analytics" ? null : (
          <>
          <Card className="border-blue-100">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-blue-950">Rango de fechas</CardTitle>
              <CardDescription>Compare semanas o meses ajustando el periodo (recomendado: últimas 8–12 semanas).</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
              <div className="grid flex-1 grid-cols-2 gap-3 sm:max-w-md">
                <div className="space-y-2">
                  <Label>Desde</Label>
                  <Input type="date" value={dashFrom} onChange={(e) => setDashFrom(e.target.value)} className="border-blue-200" />
                </div>
                <div className="space-y-2">
                  <Label>Hasta</Label>
                  <Input type="date" value={dashTo} onChange={(e) => setDashTo(e.target.value)} className="border-blue-200" />
                </div>
              </div>
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
                <Button type="button" variant="secondary" className="border border-blue-200 bg-white" onClick={loadDashboard} disabled={dashLoading}>
                  {dashLoading ? "Actualizando..." : "Actualizar gráficas"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="border-blue-300 bg-white text-blue-800 hover:bg-blue-50 gap-2"
                  disabled={!!exporting}
                  onClick={async () => {
                    setExporting("excel");
                    setDashErr("");
                    try {
                      await downloadQuickStatsExport(api, "excel", dashFrom, dashTo, (err) =>
                        setDashErr(formatApiError(err) || "No se pudieron cargar las estadísticas.")
                      );
                    } finally {
                      setExporting(null);
                    }
                  }}
                  {...excelExportButtonA11y}
                >
                  <FileSpreadsheet className="h-4 w-4 shrink-0" aria-hidden />
                  <ExcelExportLabel busy={exporting === "excel"} />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="border-blue-300 bg-white text-blue-800 hover:bg-blue-50"
                  disabled={!!exporting}
                  onClick={async () => {
                    setExporting("pdf");
                    setDashErr("");
                    try {
                      await downloadQuickStatsExport(api, "pdf", dashFrom, dashTo, (err) =>
                        setDashErr(formatApiError(err) || "No se pudieron cargar las estadísticas.")
                      );
                    } finally {
                      setExporting(null);
                    }
                  }}
                >
                  <FileDown className="mr-2 h-4 w-4 shrink-0" />
                  {exporting === "pdf" ? "Generando…" : "PDF"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {dashErr && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{dashErr}</div>
          )}

          {dashLoading && !dash ? (
            <div className="flex justify-center py-16">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
            </div>
          ) : dash ? (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <Card className="border-blue-100 bg-white">
                  <CardContent className="p-4">
                    <p className="text-xs font-medium uppercase text-blue-800/70">Células con datos</p>
                    <p className="text-2xl font-bold text-blue-950">{dash.summary.cells_with_data}</p>
                  </CardContent>
                </Card>
                <Card className="border-blue-100 bg-white">
                  <CardContent className="p-4">
                    <p className="text-xs font-medium uppercase text-blue-800/70">Promedio último reporte</p>
                    <p className="text-2xl font-bold text-blue-950">{dash.summary.church_avg_last_report}</p>
                    <p className="text-xs text-gray-500">Asistentes por célula (última semana con dato)</p>
                  </CardContent>
                </Card>
                <Card className="border-blue-100 bg-white">
                  <CardContent className="p-4">
                    <p className="text-xs font-medium uppercase text-blue-800/70">Inv. primer mes (periodo)</p>
                    <p className="text-2xl font-bold text-blue-950">{dash.summary.total_invited_first_month_period}</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid gap-4 lg:grid-cols-3">
                <Card className="border-blue-100 lg:col-span-1">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Mayor crecimiento %</CardTitle>
                    <CardDescription className="text-xs">Primer vs último total en el rango</CardDescription>
                  </CardHeader>
                  <CardContent className="max-h-64 space-y-2 overflow-y-auto text-sm">
                    {(dash.top_growth || []).length === 0 ? (
                      <p className="text-gray-500">Sin datos suficientes.</p>
                    ) : (
                      dash.top_growth.map((r, i) => (
                        <div key={r.cell_id} className="flex items-start justify-between gap-2 border-b border-blue-50 py-2">
                          <span className="font-medium text-gray-800">
                            {i + 1}. {r.cell_name}
                          </span>
                          <Badge className="shrink-0 bg-emerald-600">+{r.value}%</Badge>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
                <Card className="border-blue-100 lg:col-span-1">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Células estancadas</CardTitle>
                    <CardDescription className="text-xs">Cambio entre −5% y +5%</CardDescription>
                  </CardHeader>
                  <CardContent className="max-h-64 space-y-2 overflow-y-auto text-sm">
                    {(dash.stagnant || []).length === 0 ? (
                      <p className="text-gray-500">Ninguna en ese rango o sin historial.</p>
                    ) : (
                      dash.stagnant.map((r) => (
                        <div key={r.cell_id} className="flex items-start justify-between gap-2 border-b border-blue-50 py-2">
                          <span className="font-medium text-gray-800">{r.cell_name}</span>
                          <Badge variant="secondary" className="shrink-0">
                            Prom. {r.value}
                          </Badge>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
                <Card className="border-blue-100 lg:col-span-1">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Más invitados (1.er mes)</CardTitle>
                    <CardDescription className="text-xs">Suma en el periodo</CardDescription>
                  </CardHeader>
                  <CardContent className="max-h-64 space-y-2 overflow-y-auto text-sm">
                    {(dash.top_invited || []).length === 0 ? (
                      <p className="text-gray-500">Sin registros.</p>
                    ) : (
                      dash.top_invited.map((r, i) => (
                        <div key={r.cell_id} className="flex items-start justify-between gap-2 border-b border-blue-50 py-2">
                          <span className="font-medium text-gray-800">
                            {i + 1}. {r.cell_name}
                          </span>
                          <Badge className="shrink-0 bg-blue-600">{Math.round(r.value)}</Badge>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>

              {!perCellChartLoading ? (
                <>
                  <Card className="border-blue-100">
                    <CardHeader>
                      <CardTitle className="text-lg text-blue-950">Asistencia total por semana (iglesia)</CardTitle>
                      <CardDescription>Suma de todas las células con reporte en cada fecha.</CardDescription>
                    </CardHeader>
                    <CardContent className="h-[280px] w-full sm:h-[320px]">
                      <ResponsiveContainer key="global-line-rc" width="100%" height="100%" debounce={50}>
                        <LineChart key="global-line-lc" data={globalLine} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="fecha" tick={{ fontSize: 11 }} stroke="#64748b" />
                          <YAxis tick={{ fontSize: 11 }} stroke="#64748b" allowDecimals={false} />
                          <Tooltip
                            contentStyle={{ borderRadius: 8, border: "1px solid #bfdbfe" }}
                            formatter={(value, name) => [value, name === "asistentes" ? "Asistentes" : name]}
                          />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="asistentes"
                            name="Asistentes"
                            stroke={chartBlue}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            isAnimationActive={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card className="border-blue-100">
                    <CardHeader>
                      <CardTitle className="text-lg text-blue-950">Tendencia mensual (suma de asistentes)</CardTitle>
                    </CardHeader>
                    <CardContent className="h-[260px] w-full sm:h-[300px]">
                      <ResponsiveContainer key="monthly-bar-rc" width="100%" height="100%" debounce={50}>
                        <BarChart key="monthly-bar-lc" data={monthlyLine} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="mes" tick={{ fontSize: 11 }} stroke="#64748b" />
                          <YAxis tick={{ fontSize: 11 }} stroke="#64748b" allowDecimals={false} />
                          <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #bfdbfe" }} />
                          <Bar dataKey="asistentes" name="Asistentes" fill={chartBlue} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card className="border-blue-100">
                    <CardHeader>
                      <CardTitle className="text-lg text-blue-950">Comparativa por célula (último total reportado)</CardTitle>
                      <CardDescription>Hasta 18 células con mayor asistencia reciente en el rango.</CardDescription>
                    </CardHeader>
                    <CardContent className="h-[min(420px,60vh)] w-full min-h-[280px]">
                      <ResponsiveContainer key="compare-bar-rc" width="100%" height="100%" debounce={50}>
                        <BarChart
                          key="compare-bar-lc"
                          layout="vertical"
                          data={barData}
                          margin={{ top: 8, right: 16, left: 8, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 11 }} stroke="#64748b" allowDecimals={false} />
                          <YAxis type="category" dataKey="nombre" width={100} tick={{ fontSize: 10 }} stroke="#64748b" />
                          <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #bfdbfe" }} />
                          <Legend />
                          <Bar dataKey="ultimo" name="Último total" fill={chartBlue} radius={[0, 4, 4, 0]} isAnimationActive={false} />
                          <Bar dataKey="promedio" name="Promedio" fill={chartSoft} radius={[0, 4, 4, 0]} isAnimationActive={false} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <div className="rounded-lg border border-blue-100 bg-blue-50/30 px-4 py-10 text-center text-sm text-gray-600">
                  Actualizando gráfica por célula… las demás gráficas se ocultan un momento para evitar conflictos de renderizado.
                </div>
              )}

              <Card className="border-blue-100">
                <CardHeader className="space-y-3">
                  <div>
                    <CardTitle className="text-lg text-blue-950">Historial por célula</CardTitle>
                    <CardDescription>
                      {isSupervisorRole
                        ? "Elija «Todas mis células» (su célula, hijas y descendientes) o una célula concreta."
                        : isPastorRole
                          ? "Elija «Todas las células» o una célula para ver su línea semanal."
                          : "Elija una célula para ver su línea de crecimiento semanal."}
                    </CardDescription>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
                    <NativeSelect
                      value={chartCellId}
                      onChange={handlePerCellChartSelect}
                      placeholder="Célula"
                      className="max-w-md flex-1"
                    >
                      {isSupervisorRole && (
                        <option value={ALL_SUPERVISOR_CELLS}>Todas mis células</option>
                      )}
                      {isPastorRole && <option value={ALL_CELLS}>Todas las células</option>}
                      {(dash.per_cell || []).map((r) => (
                        <option key={r.cell_id} value={r.cell_id}>
                          {r.cell_name} ({r.weeks_reported} sem.)
                          {!perCellHasChartSeries(r) ? " · sin datos en el periodo" : ""}
                        </option>
                      ))}
                    </NativeSelect>
                    <Button
                      type="button"
                      variant="outline"
                      className="shrink-0 gap-2 border-blue-300 bg-white text-blue-800 hover:bg-blue-50"
                      disabled={perCellChartExporting || perCellChartLoading || dashLoading}
                      onClick={handleExportPerCellChartPdf}
                    >
                      <FileDown className="h-4 w-4 shrink-0" aria-hidden />
                      {perCellChartExporting ? "Generando…" : "Exportar gráfica PDF"}
                    </Button>
                  </div>
                  {perCellChartExportMsg && (
                    <p
                      className={`text-sm ${perCellChartExportMsg.type === "err" ? "text-rose-800" : "text-emerald-800"}`}
                      role="status"
                    >
                      {perCellChartExportMsg.text}
                    </p>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {!perCellChartRow ? (
                    <p className="text-gray-500">No hay datos en este periodo.</p>
                  ) : (
                    <PerCellHistoryCard
                      key={`per-cell-hist-${perCellChartMountKey}-${normCellId(displayedCellId) || "none"}`}
                      row={perCellChartRow}
                      displayedCellId={displayedCellId}
                      displayedSeries={displayedSeries}
                      isChartLoading={perCellChartLoading}
                      chartMountKey={perCellChartMountKey}
                      historialCelulaChartRef={historialCelulaChartRef}
                    />
                  )}
                </CardContent>
              </Card>
            </>
          ) : !dashErr ? (
            <p className="rounded-lg border border-blue-100 bg-blue-50/50 px-4 py-8 text-center text-sm text-gray-600">
              Pulse «Actualizar gráficas» para cargar los datos del periodo.
            </p>
          ) : null}
          </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
